/**
 * levelgen/types.ts — What a level *is*, as data.
 *
 * Everything the town used to hard-code in `config/constants.ts` — where the
 * roads run, which plots are built on, where the bin and home stand, when a car
 * comes down the street — is described here as a plain value. That is the whole
 * point of the framework: a level stops being a hand-edited config block and
 * becomes something you can generate, validate, score, and hand to the game.
 *
 * ## What is in a level, and what isn't
 *
 * A `LevelSpec` holds only the things that differ **between** levels: the shape
 * of the town and the pressure applied to the player inside it. The *feel* of
 * the game — how stiff the leash is, how a cottage is built, what clay looks
 * like — stays in `constants.ts`, shared by every level. If you find yourself
 * wanting to vary a tunable per level, ask first whether two levels really
 * should disagree about it; most of the time the answer is no, and the thing
 * you actually want is a difficulty knob (see `difficulty.ts`).
 *
 * COORDINATES: the same ground-plane `(x, y)` as everywhere else in gameplay —
 * see the coordinate rule in AGENTS.md. Rects are `{x, y, w, h}` with x/y at the
 * top-left corner, matching `systems/level.ts`.
 */
import type { Rect } from '../level';
import type { HazardType, TreeKind, VehicleKind } from '../../types';

/**
 * How hard a level is meant to be.
 *
 * Four bands rather than a continuous number, because the label is a promise to
 * the player and needs to mean the same thing every time. The number behind it
 * still exists — see `LevelRating.score` — and the bands are cut from it by
 * `classifyDifficulty()`.
 */
export type Difficulty = 'easy' | 'medium' | 'hard' | 'very-hard';

/** Every band, in ascending order. Handy for sweeping tests and level sets. */
export const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard', 'very-hard'];

/** A point on the ground plane. */
export interface Point {
  x: number;
  y: number;
}

/**
 * A road through the town. Mirrors `ROADS.SEGMENTS`.
 *
 * A road used to be an infinite line: an orientation and a position, running
 * clean from one edge of the map to the other. Giving it a **span** is what
 * makes a town stop reading as a grid, and it does so four ways at once — a
 * road that stops partway merges two blocks into one longer one, two collinear
 * segments offset by a jog read as one street winding across town, a stub is a
 * cul-de-sac, and a `lane` is a cobbled way with no carriageway at all.
 *
 * All three new fields are **optional, defaulting to what a road was before**:
 * the full map, and a street. Resolve them through `roadSpan()` / `isStreet()`
 * in `systems/roads.ts` rather than reading the fields directly.
 */
export interface RoadSpec {
  orient: 'h' | 'v';
  /** y for a horizontal road, x for a vertical one. */
  pos: number;
  /** Start along the road's own axis; absent = the map's near edge. */
  from?: number;
  /** End along the road's own axis; absent = the map's far edge. */
  to?: number;
  /** Absent = `street`. A `lane` is cobbled, walkable, and never has traffic. */
  kind?: 'street' | 'lane';
  /**
   * Carriageway width, kerb to kerb. Absent = `ROADS.WIDTH`, the ordinary
   * street — what every road was before a town had a high street.
   *
   * Read it through `carriagewayWidth()` in `systems/roads.ts`, never off the
   * field, exactly as with `from`/`to`/`kind`. `ROADS.WIDTHS` names the three
   * the generator draws from and says what each is for.
   */
  width?: number;
}

/**
 * One cottage's paint kit. A plot carries several — `systems/terrace.ts` takes
 * them in order across the frontage, so neighbours are never the same colour.
 */
export interface ToneSpec {
  color: number;
  roofColor: number;
  paint: number;
  /** `brick` walls get coursing; `render` walls stay smooth. */
  material: 'brick' | 'render';
}

/**
 * A building plot: the **collision rect** for a block of the town, which
 * `terrace.ts` then cuts into one to three individual cottages.
 *
 * Structurally identical to an entry in `BUILDINGS.LIST`, because that is
 * exactly what it becomes — the authored town is just a `LevelSpec` someone
 * typed out by hand (see `config/level.ts`).
 */
export interface PlotSpec extends Rect {
  /** Base wall height; the cottages on it step either side by `BAY_STEP`. */
  tall: number;
  /** A paved forecourt across the front. South-facing fronts only — see below. */
  patio: boolean;
  tones: ToneSpec[];
}

/** A tree: trunk centre, size multiplier, and which silhouette to stack. */
export interface TreeSpec {
  x: number;
  y: number;
  scale: number;
  kind: TreeKind;
}

/**
 * One car or van entering the map.
 *
 * A *schedule* rather than a spawn rate, and that is deliberate. Traffic is the
 * only thing in the game that can end a run outright, so "is this level
 * completable" has to be answerable before anyone plays it — and you cannot
 * answer it about a random timer. With the releases written down, the validator
 * can check that every road leaves a gap long enough to walk across (see
 * `validate.ts` → crossing window), which is the difference between a level that
 * is *probably* fair and one that is provably fair.
 */
export interface VehicleRelease {
  /** Milliseconds from the start of the run. */
  atMs: number;
  /** Index into `LevelSpec.roads`. */
  road: number;
  /** +1 = rightward/downward, −1 = leftward/upward. */
  dir: 1 | -1;
  kind: VehicleKind;
}

/** The traffic on this level: how much of it, how fast, and exactly when. */
export interface TrafficSpec {
  /** Ceiling on vehicles alive at once, whatever the schedule asks for. */
  maxVehicles: number;
  speed: number;
  /** Releases in ascending `atMs`. */
  schedule: VehicleRelease[];
  /**
   * The schedule repeats every `loopMs`, so a slow player still meets traffic.
   * Must be at least the last release plus one gap, or the loop point becomes a
   * burst — which is exactly the case the crossing-window check exists to catch.
   */
  loopMs: number;
}

/** One critter, and when it wanders into the level. */
export interface CritterRelease {
  type: HazardType;
  /**
   * Where it starts, or **omitted** for "anywhere off a building".
   *
   * Generated levels always give a point: a written-down position is what lets
   * the validator promise the run doesn't open with a fox in the walker's lap.
   * The hand-authored town never had one — `HazardManager` rolled a position at
   * construction and still does — and inventing coordinates for it here would
   * be describing a level that was never played. So the field is optional, and
   * an absent position means exactly what it used to: scatter it.
   */
  x?: number;
  y?: number;
  /**
   * Milliseconds from the start of the run before it appears. Staggering these
   * is what stops a hard level opening with every cat in town on screen at once
   * — the pressure should build rather than be handed over in the first frame.
   */
  atMs: number;
}

/** The wildlife on this level. */
export interface HazardSpec {
  spawns: CritterRelease[];
  /** Wander speed band. Faster critters are harder to shake off. */
  speedMin: number;
  speedMax: number;
  /** How often each one re-rolls its heading. */
  retargetMinMs: number;
  retargetJitterMs: number;
}

/**
 * How hard the level came out, measured from the level itself rather than from
 * the knobs that produced it.
 *
 * This is the framework's honesty check. A generator that both sets the
 * difficulty and declares it has proved nothing; `rateLevel()` re-derives the
 * score from geometry and timings that a player would actually feel, and the
 * generator only ships a level whose rating agrees with the band it was asked
 * for. See `difficulty.ts`.
 */
export interface LevelRating {
  /** 0..1, ascending with difficulty. */
  score: number;
  difficulty: Difficulty;
  /** The weighted components, for debugging a level that rates oddly. */
  parts: {
    /** Vehicle pressure: how often, how many at once, how fast. */
    traffic: number;
    /** Critter pressure: how many, and how much of it is foxes. */
    critters: number;
    /** How far the start → bin → home errand is, against the map's own size. */
    journey: number;
    /** How many carriageways that errand makes you cross. */
    crossings: number;
    /** How much town there is to get lost in. */
    town: number;
  };
}

/** A complete, playable level. */
export interface LevelSpec {
  /** The seed it was generated from. `authored` for the hand-built town. */
  seed: string;
  /** The band it was generated *for*; `rating.difficulty` must agree. */
  difficulty: Difficulty;
  /**
   * True on the guided walk — the one level that teaches the game rather than
   * testing it. `Game` runs the tutorial script on it and the menu names it
   * "Tutorial" instead of its band; everything else treats it as an ordinary
   * easy level, which is what it is. See `levelgen/tutorial.ts`.
   */
  tutorial?: boolean;
  /**
   * The whole generated extent — everything that gets **drawn**.
   *
   * Larger than the ground the walk happens on: the town carries on past the
   * playable edge so the map ends in more town rather than in fog over nothing.
   * See `bounds`, and `levelgen/surround.ts` for how the ring is grown.
   */
  world: { width: number; height: number };
  /**
   * The ground the walk happens on — a rect inside `world`.
   *
   * Everything outside it is town that exists to be looked at: drawn, but never
   * walked, never validated for reachability, and never spawned into. The
   * walker is clamped to this rather than to `world`.
   *
   * On the hand-authored town and the guided walk this is exactly `world`, so
   * those levels behave as they always have. **Traffic is the one thing that
   * deliberately keeps `world`** — a car is released out in the surround and
   * drives through, which is why it now arrives from genuinely off the map
   * rather than materialising at the edge of play.
   */
  bounds: Rect;
  roads: RoadSpec[];
  buildings: PlotSpec[];
  parks: Rect[];
  trees: TreeSpec[];
  /**
   * Every litter bin in the town. **Any** of them takes the bag, so this is a
   * list rather than the single point it used to be: one bin per town meant a
   * player carrying a bag hunting the whole map for the one object that would
   * accept it, which is a search, not a walk.
   *
   * `bins[0]` is the one the errand was *measured* against — `placeLandmarks`
   * chooses it together with home and the green so the round trip lands on the
   * difficulty budget's target length. The rest are added afterwards, and are
   * held at roughly that one's own distance from the front door (see
   * `placeBins`), so having more of them cannot quietly turn the walk the level
   * was rated for into a stroll to the end of the street.
   *
   * Never empty. Size and drop radius stay in `OBJECTIVE.BIN`.
   */
  bins: Point[];
  /** The win zone, revealed once the bag is binned. */
  home: Rect;
  /** Where the walker starts. The dog spawns at heel beside them. */
  start: Point;
  traffic: TrafficSpec;
  hazards: HazardSpec;
  rating: LevelRating;
}

/** How bad a validation finding is. Only `error` blocks a level from shipping. */
export type IssueSeverity = 'error' | 'warning';

/**
 * One thing wrong with a level.
 *
 * `code` is stable and machine-readable so tests can assert on a specific
 * failure; `message` is the human sentence, and carries the coordinates,
 * because "a building overlaps a footway" is useless without saying which.
 */
export interface LevelIssue {
  code: string;
  severity: IssueSeverity;
  message: string;
}
